import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Bell, Plus, Send, Trash2, Eye, MousePointerClick,
  AlertTriangle, Info, Megaphone, Sparkles, ShieldAlert,
  Users, Store, Link2, Image, BarChart3, X, CheckCircle2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  image_url: string | null;
  link_url: string | null;
  link_label: string | null;
  target_type: string;
  target_restaurant_ids: string[];
  is_active: boolean;
  created_at: string;
}

interface NotificationStats {
  total_targets: number;
  dismissed_count: number;
  click_count: number;
}

const TYPE_CONFIG: Record<string, { label: string; icon: any; color: string }> = {
  info: { label: "Informação", icon: Info, color: "bg-blue-500/10 text-blue-500 border-blue-500/20" },
  warning: { label: "Aviso", icon: AlertTriangle, color: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  alert: { label: "Alerta", icon: ShieldAlert, color: "bg-destructive/10 text-destructive border-destructive/20" },
  promo: { label: "Promoção", icon: Megaphone, color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  news: { label: "Novidade", icon: Sparkles, color: "bg-purple-500/10 text-purple-500 border-purple-500/20" },
};

interface Props {
  restaurants: any[];
  logAction: (action: string, entityType: string, entityId: string, details?: any) => Promise<void>;
}

const AdminNotificationsTab = ({ restaurants, logAction }: Props) => {
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<Record<string, NotificationStats>>({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("info");
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  const fetchNotifications = useCallback(async () => {
    const { data } = await supabase
      .from("admin_notifications")
      .select("*")
      .order("created_at", { ascending: false });

    const notifs = (data || []) as any as Notification[];
    setNotifications(notifs);

    // Fetch stats for each notification
    const statsMap: Record<string, NotificationStats> = {};
    for (const n of notifs) {
      const [dismissals, clicks] = await Promise.all([
        supabase.from("admin_notification_dismissals").select("id", { count: "exact", head: true }).eq("notification_id", n.id),
        supabase.from("admin_notification_clicks").select("id", { count: "exact", head: true }).eq("notification_id", n.id),
      ]);

      const totalTargets = n.target_type === "all"
        ? restaurants.length
        : (n.target_restaurant_ids?.length || 0);

      statsMap[n.id] = {
        total_targets: totalTargets,
        dismissed_count: dismissals.count || 0,
        click_count: clicks.count || 0,
      };
    }
    setStats(statsMap);
    setLoading(false);
  }, [restaurants.length]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const resetForm = () => {
    setTitle(""); setMessage(""); setType("info");
    setImageUrl(""); setLinkUrl(""); setLinkLabel("");
    setTargetType("all"); setSelectedRestaurants([]);
  };

  const handleCreate = async () => {
    if (!title.trim() || !message.trim()) {
      toast({ title: "Preencha título e mensagem", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const payload: any = {
        created_by: user!.id,
        title: title.trim(),
        message: message.trim(),
        type,
        image_url: imageUrl.trim() || null,
        link_url: linkUrl.trim() || null,
        link_label: linkLabel.trim() || null,
        target_type: targetType,
        target_restaurant_ids: targetType === "specific" ? selectedRestaurants : [],
      };

      const { error } = await supabase.from("admin_notifications").insert(payload);
      if (error) throw error;

      await logAction("create_notification", "admin_notification", "", { title, type, target_type: targetType });
      toast({ title: "Notificação enviada!", description: `Enviada para ${targetType === "all" ? "todos os restaurantes" : `${selectedRestaurants.length} restaurante(s)`}.` });
      resetForm();
      setCreateOpen(false);
      fetchNotifications();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleToggleActive = async (id: string, active: boolean) => {
    await supabase.from("admin_notifications").update({ is_active: !active }).eq("id", id);
    fetchNotifications();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("admin_notifications").delete().eq("id", id);
    await logAction("delete_notification", "admin_notification", id);
    fetchNotifications();
  };

  const toggleRestaurant = (id: string) => {
    setSelectedRestaurants(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const detailNotif = notifications.find(n => n.id === detailOpen);
  const detailStats = detailOpen ? stats[detailOpen] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Notificações
          </h2>
          <p className="text-sm text-muted-foreground">Envie alertas e comunicados para os restaurantes.</p>
        </div>
        <Button onClick={() => { resetForm(); setCreateOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" />
          Nova Notificação
        </Button>
      </div>

      {/* Stats overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total Enviadas</p>
          <p className="text-2xl font-bold font-display">{notifications.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Ativas</p>
          <p className="text-2xl font-bold font-display text-emerald-500">{notifications.filter(n => n.is_active).length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total Visualizações</p>
          <p className="text-2xl font-bold font-display text-blue-500">
            {Object.values(stats).reduce((s, st) => s + st.dismissed_count, 0)}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total Cliques</p>
          <p className="text-2xl font-bold font-display text-purple-500">
            {Object.values(stats).reduce((s, st) => s + st.click_count, 0)}
          </p>
        </Card>
      </div>

      {/* Notifications list */}
      <div className="space-y-3">
        {notifications.map(n => {
          const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.info;
          const Icon = cfg.icon;
          const st = stats[n.id];

          return (
            <Card key={n.id} className={`overflow-hidden ${!n.is_active ? "opacity-60" : ""}`}>
              <div className="flex items-start gap-4 p-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${cfg.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-sm">{n.title}</h3>
                    <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                    {n.target_type === "all" ? (
                      <Badge variant="secondary" className="text-[10px] gap-1"><Users className="h-3 w-3" />Todos</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] gap-1"><Store className="h-3 w-3" />{n.target_restaurant_ids?.length || 0} rest.</Badge>
                    )}
                    {n.link_url && <Badge variant="outline" className="text-[10px] gap-1"><Link2 className="h-3 w-3" />Link</Badge>}
                    {n.image_url && <Badge variant="outline" className="text-[10px] gap-1"><Image className="h-3 w-3" />Imagem</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.message}</p>
                  {st && (
                    <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{st.total_targets} destinatários</span>
                      <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{st.dismissed_count} visualizaram</span>
                      {n.link_url && <span className="flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{st.click_count} cliques</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetailOpen(n.id)}>
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                  <Switch checked={n.is_active} onCheckedChange={() => handleToggleActive(n.id, n.is_active)} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(n.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {!loading && notifications.length === 0 && (
          <Card className="p-8 text-center">
            <Bell className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nenhuma notificação enviada ainda.</p>
          </Card>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Nova Notificação
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <cfg.icon className="h-4 w-4" />
                        {cfg.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Título *</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título da notificação" />
            </div>
            <div>
              <Label>Mensagem *</Label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Mensagem completa..." rows={4} />
            </div>
            <div>
              <Label>URL da Imagem (opcional)</Label>
              <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>URL do Link (opcional)</Label>
                <Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <Label>Texto do Link</Label>
                <Input value={linkLabel} onChange={e => setLinkLabel(e.target.value)} placeholder="Saiba mais" />
              </div>
            </div>
            <div>
              <Label>Destinatários</Label>
              <Select value={targetType} onValueChange={setTargetType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os restaurantes</SelectItem>
                  <SelectItem value="specific">Restaurantes específicos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {targetType === "specific" && (
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-1">
                {restaurants.map(r => (
                  <button
                    key={r.id}
                    onClick={() => toggleRestaurant(r.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedRestaurants.includes(r.id)
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-secondary text-muted-foreground"
                    }`}
                  >
                    {selectedRestaurants.includes(r.id) && <CheckCircle2 className="h-4 w-4 shrink-0" />}
                    <span className="truncate">{r.name}</span>
                  </button>
                ))}
                {selectedRestaurants.length > 0 && (
                  <p className="text-xs text-muted-foreground pt-2">{selectedRestaurants.length} selecionado(s)</p>
                )}
              </div>
            )}
            <Button onClick={handleCreate} disabled={sending} className="w-full gap-2">
              <Send className="h-4 w-4" />
              {sending ? "Enviando..." : "Enviar Notificação"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail / Analytics dialog */}
      <Dialog open={!!detailOpen} onOpenChange={() => setDetailOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Análise da Notificação
            </DialogTitle>
          </DialogHeader>
          {detailNotif && detailStats && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">{detailNotif.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{detailNotif.message}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Criada em {new Date(detailNotif.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3 text-center">
                  <Users className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                  <p className="text-lg font-bold">{detailStats.total_targets}</p>
                  <p className="text-[10px] text-muted-foreground">Destinatários</p>
                </Card>
                <Card className="p-3 text-center">
                  <Eye className="h-4 w-4 mx-auto text-blue-500 mb-1" />
                  <p className="text-lg font-bold">{detailStats.dismissed_count}</p>
                  <p className="text-[10px] text-muted-foreground">Visualizaram</p>
                </Card>
                <Card className="p-3 text-center">
                  <MousePointerClick className="h-4 w-4 mx-auto text-purple-500 mb-1" />
                  <p className="text-lg font-bold">{detailStats.click_count}</p>
                  <p className="text-[10px] text-muted-foreground">Cliques</p>
                </Card>
              </div>
              {detailStats.total_targets > 0 && (
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Taxa de visualização</span>
                      <span className="font-medium">{Math.round((detailStats.dismissed_count / detailStats.total_targets) * 100)}%</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, (detailStats.dismissed_count / detailStats.total_targets) * 100)}%` }} />
                    </div>
                  </div>
                  {detailNotif.link_url && detailStats.dismissed_count > 0 && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">CTR (cliques / visualizações)</span>
                        <span className="font-medium">{Math.round((detailStats.click_count / detailStats.dismissed_count) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(100, (detailStats.click_count / detailStats.dismissed_count) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {detailNotif.link_url && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Link:</span>{" "}
                  <a href={detailNotif.link_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {detailNotif.link_label || detailNotif.link_url}
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNotificationsTab;
